import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import cn from 'classnames';

import ArrowRight from '@crehana/crehana-icons/lib/ArrowRight';

import useOnClickOutside from '../hooks/useOnClickOutside';
import transition from '../styles/transition';
import parseClassnames from '../helpers/parseClassnames';
import { CuiOptionType } from '../helpers/types';
import { KEY_CODES } from '../helpers/constants';
import {
  TextFieldBackgroundState,
  TextFieldBorderState,
} from '../TextField/types';
import { BACKGROUND_COLOR, BORDER_COLOR } from '../TextField/classnames';
import { DropdownProps } from './types';
import Menu from '../Menu';

const arrowStyles = {
  bottom: (open: boolean) =>
    open ? 'transform: rotate(-90deg);' : 'transform: rotate(90deg)',
  top: (open: boolean) =>
    !open ? 'transform: rotate(-90deg);' : 'transform: rotate(90deg)',
};

const Arrow = styled(ArrowRight)<{
  open: boolean;
  displayPosition: 'bottom' | 'top' | 'responsive';
}>`
  transition: ${transition('transform')};
  ${({ theme, open, displayPosition }) =>
    displayPosition === 'responsive'
      ? `
        ${arrowStyles.top(open)};
        ${theme.breakpoints.up('sm')} {
          ${arrowStyles.bottom(open)};
        }
      `
      : arrowStyles[displayPosition](open)}
`;

function Dropdown<OptionValueType = string>({
  options,
  selected,
  onSelected,
  label,
  labelProps = {},
  hint,
  required,
  disabled,
  full,
  sm,
  size = 4,
  open: openProp,
  onToggleOpen: onToggleOpenProp,
  selectId,
  name,
  form,
  className = '',
  clampText = true,
  dark,
  onOpen,
  'data-testid': dataTestId,
  displayPosition = 'bottom',
  inputRef,
  error,
  errorText,
  ...other
}: DropdownProps<OptionValueType>) {
  const OPTION_HEIGHT = sm ? 34 : 56;
  const [openState, setOpenState] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const setSelectElementValue = (newValue: string) => {
    if (selectId) {
      const selectElement = document.getElementById(selectId);

      if (selectElement) {
        (selectElement as HTMLSelectElement).value = newValue;
      }
    } else if (name) {
      const namedElements = Array.from(document.getElementsByName(name));
      const element = namedElements.find(e => e.tagName === 'SELECT');

      if (element) {
        (element as HTMLSelectElement).value = newValue;
      }
    }
  };

  useEffect(() => {
    if (selected && (selectId || name)) {
      setSelectElementValue(selected.value.toString());
    }
  }, []);

  const hasOpenProp = typeof openProp === 'boolean';

  const onToggleOpen = () => {
    if (!disabled) {
      const newOpenState = !openState;

      if (!hasOpenProp) {
        setOpenState(newOpenState);
        if (newOpenState && onOpen) {
          onOpen();
        }
      }
      if (typeof onToggleOpenProp === 'function') {
        onToggleOpenProp(!hasOpenProp ? newOpenState : undefined);
      }
    }
  };

  const handler = () => {
    if (openState || openProp) {
      onToggleOpen();
    }
  };

  useOnClickOutside(containerRef, handler);

  const onSelect = (option: CuiOptionType<OptionValueType>) => {
    const currentlySelected = selected && selected.value === option.value;
    let newSelected: CuiOptionType<OptionValueType> | undefined = option;

    if (!required && currentlySelected) {
      newSelected = undefined;
    }

    if (typeof onSelected === 'function' && selected !== newSelected) {
      onSelected(newSelected);
    }
    if (selectId || name) {
      setSelectElementValue(selected ? selected.value.toString() : '');
    }
    onToggleOpen();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    action: () => void,
  ) => {
    if (event.keyCode === KEY_CODES.ENTER) {
      event.stopPropagation();
      action();
    }
  };

  const open = typeof openProp === 'boolean' ? openProp : openState;

  const shownOptions = options.length >= size ? size : options.length;
  const selectedLabel = selected?.jsxLabel || selected?.label;

  const wrapperPositionClassNames = {
    bottom: 'flex-col',
    top: 'flex-col-reverse absolute bottom-0',
    responsive:
      'flex-col-reverse absolute bottom-0 sm:flex-col sm:bottom-auto sm:top-0',
  };
  let mainFontColor = dark ? 'text-white' : 'text-base-main';
  let backgroundState: TextFieldBackgroundState = dark ? 'dark' : 'light';
  let borderState: TextFieldBorderState = dark ? 'dark' : 'light';

  if (error || errorText) {
    borderState = 'error';
  }
  if (!selectedLabel) {
    mainFontColor = dark ? 'text-base-lighter' : 'text-gray-dark';
  }
  if (disabled) {
    backgroundState = dark ? 'dark-disabled' : 'light-disabled';
    borderState = 'disabled';
    mainFontColor = 'text-gray-dark';
  }

  return (
    <div
      className={parseClassnames(
        className,
        cn('inline-flex flex-col', full ? 'w-full' : 'w-full sm:w-280'),
      )}
      {...other}
      ref={containerRef}
    >
      {label && (
        <label
          className={cn(
            'font-subtitle3 mb-8',
            dark ? 'text-white' : 'text-base-main',
          )}
          {...labelProps}
        >
          {label}
        </label>
      )}
      <select
        className="hidden"
        name={name}
        id={selectId}
        form={form}
        data-testid={dataTestId}
      >
        {hint && (
          <option value="" disabled>
            {hint}
          </option>
        )}
        {options.map(opt => (
          <option key={`${selectId || name}-${opt.value}`} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className={cn('relative', sm ? 'h-32' : 'h-56')}>
        <div
          className={cn(
            'inline-flex w-full z-1',
            sm ? 'h-32' : 'h-56',
            disabled ? 'cursor-default' : 'cursor-pointer',
            wrapperPositionClassNames[displayPosition],
            { 'shadow-dropdown': open && !dark },
          )}
          style={{
            transition: transition([
              'box-shadow',
              'background-color',
              'border',
            ]),
            height: `${(open
              ? OPTION_HEIGHT * (shownOptions + 1)
              : OPTION_HEIGHT) + (!dark ? 2 : 0)}px`,
            zIndex: open ? 200 : 2,
          }}
        >
          <div
            className={cn(
              'inline-flex flex-shrink-0 justify-between items-center w-full rounded-5 border border-solid px-16',
              sm ? 'h-32' : 'h-56',
              BACKGROUND_COLOR[backgroundState],
              BORDER_COLOR[borderState],
            )}
            onClick={onToggleOpen}
            onKeyDown={e => handleKeyDown(e, onToggleOpen)}
            data-testid={dataTestId ? `${dataTestId}-button` : undefined}
          >
            <span
              className={cn('font-body3 overflow-hidden pr-16', mainFontColor, {
                'truncate break-all': sm || clampText,
              })}
              data-testid={dataTestId ? `${dataTestId}-label` : undefined}
            >
              {selectedLabel || hint}
            </span>
            <Arrow
              className={cn('fill-current', {
                [dark ? 'text-secondary-main' : 'text-primary-main']: !disabled,
                [mainFontColor]: disabled,
              })}
              ignoreColor
              size={24}
              displayPosition={displayPosition}
              open={open}
            />
          </div>
          {open && (
            <Menu
              className="z-7"
              options={options}
              selected={selected}
              onSelected={onSelect}
              full
              clampText={clampText}
              dark={dark}
              data-testid={dataTestId ? `${dataTestId}-options` : undefined}
            />
          )}
        </div>
      </div>
      {errorText && (
        <span
          className={cn(
            'font-caption mt-8',
            disabled ? 'text-gray-dark' : 'text-red-main',
          )}
          role="alert"
        >
          {errorText}
        </span>
      )}
    </div>
  );
}

export default Dropdown;
